import { useCallback, useEffect, useState } from "react";
import { NavBar } from "@/components/NavBar";
import { api, fmt, hhmm } from "@/lib/api";
import { Crown, Medal, Award } from "lucide-react";

const PERIODS = [
  ["weekly", "This week"],
  ["monthly", "This month"],
  ["alltime", "All time"],
];
const RANK_ICON = { 1: Crown, 2: Medal, 3: Award };

export default function Leaderboard() {
  const [period, setPeriod] = useState("weekly");
  const [rows, setRows] = useState([]);
  const [levels, setLevels] = useState([]);
  const [podium, setPodium] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/leaderboard?period=${period}&metric=minutes`);
    setRows(data);
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/levels").then(({ data }) => setLevels(data));
    api.get("/podium").then(({ data }) => setPodium(data));
  }, []);

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="leaderboard-page">
        <h1 className="text-4xl font-bold">Leaderboard</h1>
        {podium?.announce && (
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground" data-testid="podium-announce-text">
            {podium.announce}
          </p>
        )}

        {podium && (
          <div className="mt-10 grid gap-4 md:grid-cols-3" data-testid="podium-preview">
            {[1, 2, 3].map((rank) => {
              const s = podium.standings.find((x) => x.rank === rank);
              const prize = podium.prizes.find((p) => p.rank === rank);
              const Icon = RANK_ICON[rank];
              return (
                <div
                  key={rank}
                  data-testid={`player-podium-${rank}`}
                  className={`card-hover border p-6 ${
                    rank === 1 ? "border-primary/60 bg-primary/5" : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className={`h-6 w-6 ${rank === 1 ? "text-primary" : "text-muted-foreground"}`} />
                    <span className="mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      #{rank} · {podium.period}
                    </span>
                  </div>
                  <div className="mt-4 text-lg font-semibold">{s ? s.username : "Up for grabs"}</div>
                  <div className="mono mt-1 text-sm text-primary">
                    {s ? hhmm(s.minutes) : "no playtime logged"}
                  </div>
                  <div className="mt-4 border-t border-border pt-4 text-xs text-muted-foreground">
                    {prize?.label || `Rank ${rank} prize`} — {fmt(prize?.points || 0)} pts
                    {prize?.reward_name ? ` + ${prize.reward_name}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-2" data-testid="period-switch">
          {PERIODS.map(([v, l]) => (
            <button
              key={v}
              data-testid={`period-${v}`}
              onClick={() => setPeriod(v)}
              className={`border px-5 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
                period === v
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-8 md:grid-cols-3">
          <div className="border border-border bg-card md:col-span-2">
            {rows.length === 0 && (
              <p className="p-6 text-sm text-muted-foreground">No playtime recorded for this period yet.</p>
            )}
            {rows.map((r, i) => (
              <div key={r.username} className="flex items-center gap-4 border-b border-border p-5" data-testid={`leader-row-${i}`}>
                <span className="mono w-8 text-lg text-primary">{i + 1}</span>
                <div className="flex-1">
                  <div className="font-semibold">{r.username}</div>
                  <div className="text-xs text-muted-foreground">
                    Lv {r.level.level} · {r.level.name} · {fmt(r.lifetime_points)} pts this period
                  </div>
                </div>
                <span className="mono text-primary">{hhmm(r.playtime_minutes)}</span>
              </div>
            ))}
          </div>

          <div className="border border-border bg-card p-6" data-testid="levels-panel">
            <h3 className="text-lg font-semibold">Rank ladder</h3>
            <div className="mt-4 space-y-3 text-sm">
              {levels.map((l) => (
                <div key={l.level} className="border-b border-border pb-2">
                  <div className="flex justify-between">
                    <span>
                      <span className="mono text-primary">L{l.level}</span> {l.name}
                    </span>
                    <span className="mono text-muted-foreground">{fmt(l.min_points)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{l.perk}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
